/**
 * App section registry — ONE source of truth for the primary navigation
 * order shared by the GAS dashboard and TekStak (GAS-shell parity,
 * docs/SETTINGS-UX-REVIEW-2026-08.md §9).
 *
 * Each app maps keys to its own routes, icons, and role/feature gates —
 * chrome stays app-specific by design. What is shared is the ORDER, the
 * labels, and the one-line blurbs, so the two apps' menus and home jump
 * tiles never drift apart.
 *
 * `inTenantOnly` sections exist only inside the PANW tenant (trust
 * boundary) — TekStak must not render an entry for them.
 */

export interface AppSection {
  key: string;
  label: string;
  blurb: string;
  inTenantOnly?: boolean;
}

export const APP_SECTIONS: AppSection[] = [
  { key: 'execution', label: 'Execution', blurb: 'Emails, todos, meetings' },
  { key: 'customers', label: 'Accounts', blurb: 'Dash, org chart, contacts' },
  { key: 'capture', label: 'Capture', blurb: 'Raw notes & Zoom drops — stays in Drive', inTenantOnly: true },
  { key: 'tasks', label: 'Tasks', blurb: 'Capture, triage, execute' },
  { key: 'contacts', label: 'Contacts', blurb: 'People across your accounts' },
  { key: 'opportunities', label: 'Opportunities', blurb: 'Deals, goals, SFDC notes' },
  { key: 'strategies', label: 'Strategies', blurb: '30-60-90, ASRs, TCO' },
  { key: 'vendors', label: 'Vendors', blurb: 'Spend, heatmaps, renewals' },
  // Department- and contact-tagged news about CA agencies (docs/DEPARTMENT-
  // NEWS-FEED.md). Both hosts render it: TekStak /news, GAS News view.
  { key: 'news', label: 'News', blurb: 'What changed at your agencies' },
  { key: 'quotes', label: 'Deals', blurb: 'Deal construction & pricing' },
  { key: 'presentations', label: 'Presentations', blurb: 'Pitches & narratives' },
  { key: 'tools', label: 'Tools', blurb: 'Calculators & BOMs' },
  { key: 'review', label: 'Review', blurb: 'Data-quality triage', inTenantOnly: true },
  // Cross-account catalogue of the executive summaries approved in TekStak
  // (boundary decision #8). The per-account view lives on the Accounts tab
  // strip; this is the "everything, as I go along" surface. inTenantOnly:
  // TekStak's equivalent is the Recordings page, which is the source, not a
  // second catalogue.
  { key: 'meetings', label: 'Meetings', blurb: 'Executive summaries by account', inTenantOnly: true },
  // Official PANW logo + icon library with copy-for-slides affordances.
  // inTenantOnly for a different reason than the trust boundary: brand
  // material is PANW-entangled (DECISIONS.md D-020) and must never ship
  // with TekStak-the-product, so TekStak renders no entry for it.
  { key: 'brand', label: 'Brand', blurb: 'Official logos & icons', inTenantOnly: true },
];

/** Convenience lookup: key → section. */
export const APP_SECTION_MAP: Record<string, AppSection> = Object.fromEntries(
  APP_SECTIONS.map((s) => [s.key, s])
);
